import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { Send, Volume2, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LANG_VOICE = {
  en: 'en-US', es: 'es-ES', fr: 'fr-FR', it: 'it-IT', de: 'de-DE', ja: 'ja-JP',
};

const LEVELS = [
  { id: 'beginner', label: 'Nybörjare', emoji: '🌱' },
  { id: 'intermediate', label: 'Mellan', emoji: '🌿' },
  { id: 'advanced', label: 'Avancerad', emoji: '🌳' },
];

function speak(text, langCode) {
  if (!window.speechSynthesis || !text) return;
  const u = new SpeechSynthesisUtterance(text);
  u.lang = LANG_VOICE[langCode] || 'en-US';
  u.rate = 0.9;
  window.speechSynthesis.cancel();
  window.speechSynthesis.speak(u);
}

export default function PetLangChat({ language, languageLabel }) {
  const storageKey = `petlang_chat_${language}`;
  const levelKey = `petlang_level_${language}`;
  const [messages, setMessages] = useState(() => {
    try { return JSON.parse(localStorage.getItem(storageKey) || '[]'); } catch { return []; }
  });
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [level, setLevel] = useState(() => localStorage.getItem(levelKey) || 'beginner');
  const [petName, setPetName] = useState('Mango');
  const scrollerRef = useRef(null);

  // Reset history when switching language
  useEffect(() => {
    try { setMessages(JSON.parse(localStorage.getItem(storageKey) || '[]')); } catch { setMessages([]); }
    setLevel(localStorage.getItem(levelKey) || 'beginner');
  }, [language, storageKey, levelKey]);

  useEffect(() => {
    localStorage.setItem(storageKey, JSON.stringify(messages.slice(-30)));
  }, [messages, storageKey]);

  useEffect(() => {
    localStorage.setItem(levelKey, level);
  }, [level, levelKey]);

  useEffect(() => {
    if (scrollerRef.current) scrollerRef.current.scrollTop = scrollerRef.current.scrollHeight;
  }, [messages, sending]);

  const send = useCallback(async () => {
    const text = input.trim();
    if (!text || sending) return;
    setSending(true);
    const newHist = [...messages, { role: 'user', text }];
    setMessages(newHist);
    setInput('');
    try {
      const { data } = await axios.post(`${API}/pet-lang/chat`, {
        language, level, message: text,
        history: messages.slice(-10).map((m) => ({ role: m.role, text: m.role === 'user' ? m.text : (m.reply || m.text) })),
      }, { withCredentials: true });
      setPetName(data.pet_name || 'Mango');
      setMessages([...newHist, {
        role: 'them',
        reply: data.reply || '',
        correction: data.correction || '',
        text: data.reply || '',
      }]);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Kunde inte skicka');
    } finally {
      setSending(false);
    }
  }, [input, sending, messages, language, level]);

  const clearChat = () => {
    if (!window.confirm(`Rensa hela chatten med ${petName}?`)) return;
    setMessages([]);
    localStorage.removeItem(storageKey);
    toast.success('Rensat 🐾');
  };

  return (
    <div className="space-y-3" data-testid={`petlang-${language}`}>
      <div className="neo-card p-4 bg-gradient-to-br from-[#FFE8D6] to-[#BBF7D0]">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-full border-4 border-black bg-gradient-to-br from-amber-300 to-orange-400 flex items-center justify-center text-2xl shadow-md">
              🐶
            </div>
            <div>
              <h3 className="text-lg font-bold font-['Fredoka']">{petName} pratar {languageLabel} 🐾</h3>
              <p className="text-[11px] opacity-70 leading-tight max-w-xs">
                Skriv på {languageLabel} (eller svenska om du fastnar). Hen svarar och rättar dig snällt ✨
              </p>
            </div>
          </div>
          <button onClick={clearChat} className="neo-btn px-3 py-2 bg-[#FFB3B3] text-xs font-bold flex items-center gap-1"
            data-testid="petlang-clear">
            <Trash2 className="w-3.5 h-3.5" /> Rensa
          </button>
        </div>

        <div className="flex gap-1.5 mt-3 flex-wrap">
          {LEVELS.map((l) => (
            <button key={l.id} onClick={() => setLevel(l.id)}
              className={`px-3 py-1 rounded-full border-2 border-black text-[11px] font-bold transition ${
                level === l.id ? 'bg-white scale-105' : 'bg-white/60 opacity-80'
              }`}
              data-testid={`petlang-level-${l.id}`}>
              {l.emoji} {l.label}
            </button>
          ))}
        </div>
      </div>

      <div ref={scrollerRef}
        className="neo-card p-4 bg-[#FFFAF7] dark:bg-[#1f1428] h-[55vh] overflow-y-auto space-y-3"
        data-testid="petlang-messages">
        {messages.length === 0 && (
          <div className="h-full flex flex-col items-center justify-center text-center px-4">
            <div className="text-5xl mb-2">🐶✨</div>
            <p className="font-bold font-['Fredoka']">Säg hej till {petName}!</p>
            <p className="text-xs opacity-70 mt-1 max-w-sm">
              T.ex: &quot;Hola, ¿cómo estás?&quot; eller &quot;Hej Mango, vad gör du?&quot; — hen översätter och rättar.
            </p>
          </div>
        )}
        {messages.map((m, i) => (
          <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}
            data-testid={`petlang-msg-${i}`}>
            {m.role === 'user' ? (
              <div className="max-w-[78%] px-4 py-2.5 rounded-2xl border-2 border-black shadow-[2px_2px_0_#000] text-sm font-['Nunito'] whitespace-pre-wrap break-words bg-[#FFC8DD] rounded-br-sm">
                {m.text}
              </div>
            ) : (
              <div className="max-w-[85%] space-y-1.5">
                <div className="px-4 py-2.5 rounded-2xl border-2 border-black shadow-[2px_2px_0_#000] text-sm font-['Nunito'] whitespace-pre-wrap break-words bg-[#FFE8D6] rounded-bl-sm relative group">
                  <p>{m.reply}</p>
                  <button onClick={() => speak(m.reply, language)}
                    className="absolute -top-2 -right-2 neo-btn p-1.5 bg-white opacity-0 group-hover:opacity-100 transition"
                    title="Lyssna"
                    data-testid={`petlang-speak-${i}`}>
                    <Volume2 className="w-3 h-3" />
                  </button>
                </div>
                {m.correction && (
                  <div className="px-3 py-2 rounded-xl border-2 border-dashed border-amber-500 bg-[#FEF3C7] text-[12px] italic">
                    {m.correction}
                  </div>
                )}
              </div>
            )}
          </div>
        ))}
        {sending && (
          <div className="flex justify-start">
            <div className="px-4 py-2.5 rounded-2xl border-2 border-black bg-white shadow-[2px_2px_0_#000] text-sm italic opacity-70">
              {petName} tänker…
            </div>
          </div>
        )}
      </div>

      <div className="flex gap-2 items-end">
        <textarea value={input} onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }}
          placeholder={`Skriv något på ${languageLabel} (eller svenska)…`}
          rows={2}
          className="flex-1 px-3 py-2 rounded-2xl border-2 border-black bg-white dark:bg-[#2d2d54] text-sm resize-none"
          data-testid="petlang-input" />
        <button onClick={send} disabled={sending || !input.trim()}
          className="neo-btn px-4 py-3 bg-gradient-to-r from-amber-400 to-emerald-400 text-white font-bold flex items-center gap-1 disabled:opacity-50"
          data-testid="petlang-send-btn">
          <Send className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
